import { FLAGMINT_CLIENT, FLAGMINT_INIT, FLAGMINT_READY } from 'flagmint-vuejs-feature-flags'

// With the SDK disabled its client never resolves ready(), so treat "off" as ready:
// getFlag() then serves fallbacks instead of leaving flag-gated UI stuck on a loader.
const flagmintEnabled = process.env.VUE_APP_ENABLE_FLAGMINT === 'true'

/**
 * Reusable mixin for Flagmint feature flag readiness.
 *
 * The SDK's own useFlagsMixin ships as a Vue 2 build (it needs the $flagmint*
 * prototype properties the plugin only defines on Vue 2), so this mixin reads the
 * refs the plugin provides on the Vue 3 app instead. Options-API injections unwrap
 * refs, so isFlagReady/flagClient stay reactive.
 *
 * Provides:
 *  - isFlagReady, flagClient, flags, getFlag(), track(), trackError(), updateFlagmintContext()
 *  - waitForFlagsReady(timeoutMs) — awaitable in mounted(), resolves when
 *    isFlagReady is true or the timeout fires (whichever comes first)
 *  - onFlagmintReady() — empty hook; override in any consuming component to react once
 *    the client is usable, whether it was up before this component mounted or arrived
 *    later. Called at most once, and never without a client.
 */
export default {
  inject: {
    flagmintReady: { from: FLAGMINT_READY, default: false },
    flagClient: { from: FLAGMINT_CLIENT, default: null },
    flagmintInit: { from: FLAGMINT_INIT, default: null }
  },

  data () {
    return {
      // Reactive snapshot — updated on every push.
      flags: {},
      flagmintUnsubscribe: null,
      flagmintReadyNotified: false
    }
  },

  computed: {
    isFlagReady () {
      return !flagmintEnabled || this.flagmintReady === true
    }
  },

  created () {
    if (this.isFlagReady) {
      this.startFlagSubscription()
    } else if (typeof this.flagmintInit === 'function') {
      this.flagmintInit().then(() => this.startFlagSubscription()).catch(() => {})
    }
  },

  mounted () {
    // The isFlagReady watcher only fires on a change, so a component created after the
    // client was already up would never hear about it. Most components are: the client
    // resolves during startup, long before anything past the login screen mounts.
    this.notifyFlagmintReady()
  },

  beforeUnmount () {
    // Only drop our subscription — never destroy the shared client.
    if (this.flagmintUnsubscribe) {
      this.flagmintUnsubscribe()
      this.flagmintUnsubscribe = null
    }
  },

  watch: {
    isFlagReady (ready) {
      if (ready) {
        this.startFlagSubscription()
        this.notifyFlagmintReady()
      }
    }
  },

  methods: {
    startFlagSubscription () {
      if (!this.flagClient || this.flagmintUnsubscribe) {
        return
      }
      // subscribe() calls back immediately with the current flags, then on every push.
      this.flagmintUnsubscribe = this.flagClient.subscribe((updatedFlags) => {
        this.flags = updatedFlags
      })
    },

    getFlag (key, fallback) {
      if (this.flagClient && typeof this.flagClient.getFlag === 'function') {
        return this.flagClient.getFlag(key, fallback)
      }
      return this.flags[key] ?? fallback
    },

    /**
     * Custom / conversion event for A/B outcomes (Flagmint Analytics Tracking).
     * Prefer *_success / *_failure naming so Metrics conversion stays consistent.
     */
    track (flagKey, eventName, extra) {
      if (this.flagClient && typeof this.flagClient.track === 'function') {
        this.flagClient.track(flagKey, eventName, extra)
      }
    },

    /**
     * App-error event attributed to a flag variation (feeds Flagmint guardrail alarms).
     */
    trackError (flagKey, error, extra) {
      if (this.flagClient && typeof this.flagClient.trackError === 'function') {
        this.flagClient.trackError(flagKey, error, extra)
      }
    },

    async updateFlagmintContext (context) {
      if (this.flagClient && typeof this.flagClient.updateContext === 'function') {
        await this.flagClient.updateContext(JSON.parse(JSON.stringify(context)))
      } else {
        console.warn('[Flagmint] updateFlagmintContext called before client was ready.')
      }
    },

    /**
     * Override in any consuming component to handle late flag resolution.
     * Called both when flags are ready before mount AND when they resolve
     * after a waitForFlagsReady timeout.
     */
    onFlagmintReady () {},

    /**
     * Fire onFlagmintReady() exactly once, and only with a usable client — consumers use
     * it to call updateContext/track, which need one. A readiness timeout with no client
     * is not a ready signal.
     */
    notifyFlagmintReady () {
      if (this.flagmintReadyNotified || !this.flagClient) {
        return
      }
      this.flagmintReadyNotified = true
      this.onFlagmintReady()
    },

    /**
     * Awaitable guard for mounted(). Resolves as soon as isFlagReady is true,
     * or after timeoutMs if the SDK never responds — whichever comes first.
     *
     * On timeout we log a warning and proceed with flag defaults.
     *
     * @param {number} timeoutMs - max wait in ms (default 3000)
     * @returns {Promise<void>}
     */
    waitForFlagsReady (timeoutMs = 3000) {
      if (this.isFlagReady) return Promise.resolve()

      return new Promise(resolve => {
        const timer = setTimeout(() => {
          unwatch()
          console.warn(
            `[FlagmintMixin] Flagmint did not become ready within ${timeoutMs}ms — ` +
            'proceeding with flag defaults.'
          )
          resolve()
        }, timeoutMs)

        const unwatch = this.$watch('isFlagReady', ready => {
          if (ready) {
            clearTimeout(timer)
            unwatch()
            resolve()
          }
        })
      })
    }
  }
}
